import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { TrainingModuleDto, TrainingTopicDto } from '../../../api/types/dtos/training';
import {
  useTrainingTopics,
  useUpdateTrainingTopic,
} from '../../../api/hooks/useTrainingTopics';
import { useDeleteTrainingModule } from '../../../api/hooks/useTrainingModules';
import { useOrganization } from '../../../contexts/OrganizationContext';
import TopicAccordion from './TopicAccordion';
import SequenceControls from './SequenceControls';
import EditModuleModal from '../modals/EditModuleModal';
import CreateTopicModal from '../modals/CreateTopicModal';
import { canMoveUp, canMoveDown, moveUp, moveDown } from '../utils/sequenceHelpers';

interface ModuleAccordionProps {
  module: TrainingModuleDto;
  allModules: TrainingModuleDto[];
  trainingId: string;
  onMoveUp: (module: TrainingModuleDto) => void;
  onMoveDown: (module: TrainingModuleDto) => void;
}

const ModuleAccordion: React.FC<ModuleAccordionProps> = ({
  module,
  allModules,
  trainingId,
  onMoveUp,
  onMoveDown,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createTopicModalOpen, setCreateTopicModalOpen] = useState(false);
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();

  const { data: topics = [], isLoading: topicsLoading } = useTrainingTopics(
    selectedOrganization?.id || '',
    trainingId,
    module.id,
    { isActive: true }
  );

  const deleteModule = useDeleteTrainingModule(
    selectedOrganization?.id || '',
    trainingId
  );

  const updateTopic = useUpdateTrainingTopic(
    selectedOrganization?.id || '',
    trainingId,
    module.id
  );

  const handleDeleteModule = async () => {
    try {
      await deleteModule.mutateAsync(module.id);
      enqueueSnackbar('Módulo eliminado exitosamente', { variant: 'success' });
      setDeleteDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('Error al eliminar el módulo', { variant: 'error' });
      console.error('Error deleting module:', error);
    }
  };

  const handleMoveTopicUp = async (topic: TrainingTopicDto) => {
    const result = moveUp(topic, topics);
    if (!result) return;

    try {
      await Promise.all([
        updateTopic.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            sequence: result.item.sequence,
            progressContribution: result.item.progressContribution,
            moduleId: result.item.moduleId,
            logo: result.item.logo,
            isActive: result.item.isActive,
          },
        }),
        ...(result.swapItem
          ? [
              updateTopic.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  sequence: result.swapItem.sequence,
                  progressContribution: result.swapItem.progressContribution,
                  moduleId: result.swapItem.moduleId,
                  logo: result.swapItem.logo,
                  isActive: result.swapItem.isActive,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const handleMoveTopicDown = async (topic: TrainingTopicDto) => {
    const result = moveDown(topic, topics);
    if (!result) return;

    try {
      await Promise.all([
        updateTopic.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            sequence: result.item.sequence,
            progressContribution: result.item.progressContribution,
            moduleId: result.item.moduleId,
            logo: result.item.logo,
            isActive: result.item.isActive,
          },
        }),
        ...(result.swapItem
          ? [
              updateTopic.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  sequence: result.swapItem.sequence,
                  progressContribution: result.swapItem.progressContribution,
                  moduleId: result.swapItem.moduleId,
                  logo: result.swapItem.logo,
                  isActive: result.swapItem.isActive,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const sortedTopics = [...topics].sort((a, b) => a.sequence - b.sequence);

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          mb: 2,
          border: '2px solid',
          borderColor: 'primary.light',
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              justifyContent: 'space-between',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography variant="h6">{module.name}</Typography>
            <Chip
              label={`Secuencia ${module.sequence}`}
              size="small"
              sx={{ bgcolor: 'white', color: 'primary.main' }}
            />
            <Chip
              label={`${module.progressContribution}% de progreso`}
              size="small"
              sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}
            />
            <Chip
              label={`${topics.length} temas`}
              size="small"
              sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}
            />
          </Box>
          <Box
            sx={{ display: 'flex', gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <SequenceControls
              canMoveUp={canMoveUp(module, allModules)}
              canMoveDown={canMoveDown(module, allModules)}
              onMoveUp={() => onMoveUp(module)}
              onMoveDown={() => onMoveDown(module)}
            />
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() => setEditModalOpen(true)}
                sx={{ color: 'white' }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ color: 'white' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {module.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {module.description}
            </Typography>
          )}
          {module.skillName && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Habilidad:</strong> {module.skillName}
            </Typography>
          )}

          {topicsLoading ? (
            <Typography>Cargando temas...</Typography>
          ) : (
            <>
              {sortedTopics.map((topic) => (
                <TopicAccordion
                  key={topic.id}
                  topic={topic}
                  allTopics={sortedTopics}
                  trainingId={trainingId}
                  moduleId={module.id}
                  onMoveUp={handleMoveTopicUp}
                  onMoveDown={handleMoveTopicDown}
                />
              ))}

              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setCreateTopicModalOpen(true)}
              >
                Agregar Tema
              </Button>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <EditModuleModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        module={module}
        trainingId={trainingId}
      />

      <CreateTopicModal
        open={createTopicModalOpen}
        onClose={() => setCreateTopicModalOpen(false)}
        trainingId={trainingId}
        moduleId={module.id}
        existingTopics={topics}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el módulo &quot;{module.name}&quot;? Esto también
            eliminará todos los temas y contenidos asociados. Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteModule} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModuleAccordion;

